import { FaChevronDown } from 'react-icons/fa';
import { LuFactory } from 'react-icons/lu';

import React, { useEffect, useState, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import axios from 'axios'

const EditTemplate = () => {
  const axiosPrivate = useAxiosPrivate()
  const [checklist, setChecklist] = useState([])
  const [industry, setIndustry] = useState("")
  const location = useLocation()
  const from = location.state?.from?.pathname || '/settings';
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  useEffect(() => {
    let isMounted = true
    const controller = new AbortController()

    const fetchData = async () => {
      setIsLoading(true)
      try {
        const templateResponse = await axiosPrivate.get(
          'template/retrieve-template',
          {
            signal: controller.signal,
          },
        )

        if (isMounted) {
          setIndustry(templateResponse.data.industry)
          setChecklist(templateResponse.data.checklist)
        }
      } catch (err) {
        if (axios.isCancel(err)) {
          // Log for debugging purposes
          // console.log('Request cancelled:', err.message);
        } else {
          console.error('Request failed:', err)
          // Only navigate if the error was not a cancellation
          // Check for a 403 status code specifically
          if (err.response && err.response.status === 403) {
            navigate('/sign-in', { state: { from: location }, replace: true })
          }
        }
      }

      setIsLoading(false)
    }

    fetchData()

    return () => {
      isMounted = false
      controller.abort()
      // console.log('Cleanup: Cancelled any ongoing requests.');
    }
  }, [navigate, location, axiosPrivate])

  // Group checklists by scope
  const groupedByScope = useMemo(() => {
    if (!Array.isArray(checklist)) return {}; // Safety guard
    return checklist.reduce((acc, item) => {
      if (!acc[item.scope]) acc[item.scope] = [];
      acc[item.scope].push(item);
      //return {1: Array(4), 2: Array(2), 3: Array(15)}
      return acc;
    }, {});
  }, [checklist]);


  //To handle the ticking/unticking from the user
  const handleToggle = (id, checked) => {
    setChecklist(prev =>
      prev.map(item =>
        item.id === id ? { ...item, checked } : item
      )
    );
  }

  const handleSave = async () => {
    const selectedCategoryIds = checklist
      .filter(item => item.checked)
      .map(item => item.id);

    try {
      const response = await axiosPrivate.put('/template/edit-template', {
        selectedCategoryIds,
      });

      navigate(from, { replace: true }); // Un-comment this line to use dynamic navigation
      return response.data
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error('Submission failed:', err)
        const errorMsg = err.response?.data.message || 'An error occurred.'
        if (err.response?.status === 403) {
          navigate('/sign-in', { state: { from: location }, replace: true })
        } else {
          alert(`Error: ${errorMsg}`)
        }
      }
    }
  }


  if (isLoading) {
    return <div>Loading...</div> // Or any loading spinner
  }
  return (
       <div className="p-6 md:p-10 max-w-6xl w-full mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary text-primary-foreground">
          <LuFactory className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">
            Edit Template
          </h1>
          <p className="text-sm text-muted-foreground">
            Industry: {industry}
          </p>
        </div>
      </div>
      
      <div className="space-y-3">
        {Object.entries(groupedByScope).map(([scope, items]) => (
          <div
            key={scope}
            className="mt-2 mb-4 border rounded-lg"
          >
            <details key={scope} open className="group rounded-lg border bg-card overflow-hidden">
              <summary className="w-full flex justify-between items-center p-4 hover:bg-accent/50 transition-colors cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                <h4 className="font-display font-semibold text-sm text-card-foreground">
                  Scope {scope} Categories
                </h4>
                <FaChevronDown className="w-4 h-4 text-muted-foreground transition-transform duration-200 group-open:rotate-180" />
              </summary>
              <div className="px-4 pb-3 space-y-1">
                {items.map((item) => (
                  <label
                    key={item.id}
                    className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-accent/30 transition-colors cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={item.checked}
                      onChange={(e) => handleToggle(item.id, e.target.checked)}

                    />
                    <span className="text-sm text-foreground">
                      {item.name}
                    </span>
                  </label>
                ))}
              </div>
            </details>
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-6">
        <button onClick={() => setShowDeleteModal(true)} className="mt-4 px-4 py-2 bg-blue-500 text-white w-1/6 rounded-lg hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-800">
          Save Changes
        </button>
      </div>
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex justify-center items-center p-4">
          <div className="bg-white dark:bg-gray-800 w-full max-w-sm mx-auto rounded shadow-lg overflow-hidden p-4">
            <p className="text-gray-800 dark:text-gray-200 text-center text-lg md:text-base">
              Are you sure you want to save changes?
            </p>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="bg-gray-300 dark:bg-gray-600 dark:text-gray-200 px-4 py-2 rounded mr-2 text-sm md:text-base hover:bg-gray-400 dark:hover:bg-gray-700 transition duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="bg-blue-500 text-white px-4 py-2 rounded text-sm md:text-base hover:bg-blue-600 transition duration-300"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

};

export default EditTemplate;
